import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Moon, Save, Copy, Archive, ChevronDown, ChevronRight,
  Plane, BedDouble, Car, Package, Tag, AlignLeft,
  CheckCircle, XCircle, Plus, Trash2, ArrowLeft,
} from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import UmrahQuotationPanel from "@/components/UmrahQuotationPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_ACCOMMODATION = {
  hotel_name: "", city: "", check_in: "", check_out: "", nights: 0,
  star_rating: "", room_type: "", meal_plan: "", breakfast_included: false,
  distance_from_haram: "",
  supplier: "", supplier_reference: "", supplier_cost_per_person: "", notes: "",
};
const EMPTY_FLIGHT = {
  outbound_route: "", return_route: "", airline: "", flight_numbers: "",
  departure_airport: "", arrival_airport: "", outbound_date: "", return_date: "",
  supplier: "", supplier_cost_per_person: "", notes: "",
};
const EMPTY_TRANSPORT = {
  transport_type: "", route: "", date: "", vehicle_type: "", capacity: "",
  supplier: "", supplier_reference: "", supplier_cost_per_person: "", selling_allocation_per_person: "", notes: "",
};
const EMPTY_SERVICE = {
  service_name: "", description: "", included: true,
  supplier: "", supplier_cost_per_person: "", selling_allocation_per_person: "",
};
const EMPTY_OCC = { enabled: false, selling_price: "", supplier_cost: "" };

const OCC_CAPACITY = { single: 1, double: 2, triple: 3, quad: 4 };
const OCC_LABEL    = { single: "Single", double: "Double", triple: "Triple", quad: "Quad" };

const INITIAL_FORM = {
  package_name: "",
  status: "draft",
  category: "comprehensive",
  flexible_dates: false,
  travel_start_date: "",
  travel_end_date: "",
  public_visible: false,
  makkah: { ...EMPTY_ACCOMMODATION },
  madinah: { ...EMPTY_ACCOMMODATION },
  visa_included: false,
  visa_supplier_cost_per_person: "",
  flights_included: false,
  flight: { ...EMPTY_FLIGHT },
  transport_included: false,
  transport_components: [],
  other_services: [],
  occupancy_single: { ...EMPTY_OCC },
  occupancy_double: { ...EMPTY_OCC },
  occupancy_triple: { ...EMPTY_OCC },
  occupancy_quad: { ...EMPTY_OCC },
  inclusions: [],
  exclusions: [],
  terms_and_conditions: "",
  cancellation_policy: "",
  payment_terms: "",
  validity_date: "",
  itinerary_notes: "",
  internal_notes: "",
};

function calcNights(ci, co) {
  if (!ci || !co) return 0;
  try { return Math.max(0, Math.round((new Date(co) - new Date(ci)) / 86400000)); }
  catch { return 0; }
}

function fmtMoney(val) {
  const n = parseFloat(val);
  return isNaN(n) ? "—" : `€${n.toFixed(2)}`;
}

function calcOccupancyProfit(occ) {
  if (!occ?.enabled || !occ?.selling_price || !occ?.supplier_cost) return null;
  return parseFloat(occ.selling_price) - parseFloat(occ.supplier_cost);
}

// ── Itinerary generator ───────────────────────────────────────────────────────
function fmtIsoDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso + "T12:00:00Z").toLocaleDateString("en-GB", {
      day: "numeric", month: "long", year: "numeric",
    });
  } catch { return iso; }
}

function generateItinerary(form) {
  const lines = [];
  const push = (s) => lines.push(s);

  const startDate = form.travel_start_date ? new Date(form.travel_start_date + "T12:00:00Z") : null;
  const makkahCI  = form.makkah?.check_in  ? new Date(form.makkah.check_in  + "T12:00:00Z") : null;
  const makkahCO  = form.makkah?.check_out ? new Date(form.makkah.check_out + "T12:00:00Z") : null;
  const madinahCI = form.madinah?.check_in  ? new Date(form.madinah.check_in  + "T12:00:00Z") : null;
  const madinahCO = form.madinah?.check_out ? new Date(form.madinah.check_out + "T12:00:00Z") : null;
  const returnDate = form.flight?.return_date ? new Date(form.flight.return_date + "T12:00:00Z") : null;

  // Determine first day
  const day1 = makkahCI || startDate;

  // Map dates to transport descriptions
  const transportByDate = {};
  (form.transport_components || []).forEach(t => {
    if (t.route && t.date) {
      const key = t.date;
      if (!transportByDate[key]) transportByDate[key] = [];
      transportByDate[key].push(t.route);
    }
  });

  let dayNum = 0;

  // Day 1 — Arrival / Departure flight / Makkah check-in
  if (day1) {
    dayNum++;
    const label = fmtIsoDate(form.makkah?.check_in || form.travel_start_date);
    push(`DAY ${dayNum} — ARRIVAL / MAKKAH`);
    push(label);
    push("");
    if (form.flights_included && form.flight?.outbound_route) {
      push(`Outbound flight: ${form.flight.outbound_route}.`);
      if (form.flight.airline) push(`Airline: ${form.flight.airline}.`);
    }
    const arrivalTransports = Object.entries(transportByDate)
      .filter(([d]) => d === form.makkah?.check_in)
      .flatMap(([, rs]) => rs);
    arrivalTransports.forEach(r => push(`Transfer: ${r}.`));
    if (!arrivalTransports.length && form.transport_included) {
      push("Transfer from arrival airport to Makkah.");
    }
    if (form.makkah?.hotel_name) {
      push(`Check-in at ${form.makkah.hotel_name}${form.makkah.city ? `, ${form.makkah.city}` : ""}.`);
    }
  }

  // Makkah stay days
  if (makkahCI && makkahCO) {
    const nights = Math.round((makkahCO - makkahCI) / 86400000);
    for (let n = 1; n < nights; n++) {
      dayNum++;
      const dayDate = new Date(makkahCI);
      dayDate.setUTCDate(dayDate.getUTCDate() + n);
      const dayIso = dayDate.toISOString().slice(0, 10);
      push("");
      push(`DAY ${dayNum} — MAKKAH`);
      push(fmtIsoDate(dayIso));
      push("");
      push(`Accommodation: ${form.makkah?.hotel_name || "Makkah hotel"}.`);
      const dayTransports = transportByDate[dayIso] || [];
      dayTransports.forEach(r => push(`Transfer: ${r}.`));
      push("Free time / personal arrangements.");
    }
  }

  // Transfer day: Makkah → Madinah
  if (makkahCO && madinahCI) {
    const transferDate = form.makkah.check_out;
    dayNum++;
    push("");
    push(`DAY ${dayNum} — TRANSFER TO MADINAH`);
    push(fmtIsoDate(transferDate));
    push("");
    push(`Check out from ${form.makkah?.hotel_name || "Makkah hotel"}.`);
    const transferTransports = (transportByDate[transferDate] || []);
    if (transferTransports.length) {
      transferTransports.forEach(r => push(`Transfer: ${r}.`));
    } else if (form.transport_included) {
      push("Transfer from Makkah to Madinah.");
    }
    if (form.madinah?.hotel_name) {
      push(`Check-in at ${form.madinah.hotel_name}${form.madinah.city ? `, ${form.madinah.city}` : ""}.`);
    }
  }

  // Madinah stay days
  if (madinahCI && madinahCO) {
    const nights = Math.round((madinahCO - madinahCI) / 86400000);
    for (let n = 1; n < nights; n++) {
      dayNum++;
      const dayDate = new Date(madinahCI);
      dayDate.setUTCDate(dayDate.getUTCDate() + n);
      const dayIso = dayDate.toISOString().slice(0, 10);
      push("");
      push(`DAY ${dayNum} — MADINAH`);
      push(fmtIsoDate(dayIso));
      push("");
      push(`Accommodation: ${form.madinah?.hotel_name || "Madinah hotel"}.`);
      const dayTransports = transportByDate[dayIso] || [];
      dayTransports.forEach(r => push(`Transfer: ${r}.`));
      push("Free time / personal arrangements.");
    }
  }

  // Departure day
  const departureDate = form.madinah?.check_out || form.travel_end_date || form.flight?.return_date;
  if (departureDate) {
    dayNum++;
    push("");
    push(`DAY ${dayNum} — DEPARTURE`);
    push(fmtIsoDate(departureDate));
    push("");
    if (form.madinah?.hotel_name) {
      push(`Check out from ${form.madinah.hotel_name}.`);
    }
    const deptTransports = transportByDate[departureDate] || [];
    if (deptTransports.length) {
      deptTransports.forEach(r => push(`Transfer: ${r}.`));
    } else if (form.transport_included) {
      push("Transfer to departure airport.");
    }
    if (form.flights_included && form.flight?.return_route) {
      push(`Return flight: ${form.flight.return_route}.`);
      if (form.flight.airline) push(`Airline: ${form.flight.airline}.`);
    }
  }

  return lines.join("\n");
}

function Section({ title, icon: Icon, open, onToggle, children, accent = "amber" }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className={`w-full flex items-center justify-between px-5 py-4 text-left hover:bg-${accent}-50 transition-colors`}
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className={`w-5 h-5 text-${accent}-600 shrink-0`} />}
          <span className={`font-semibold text-${accent}-900`}>{title}</span>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
      </button>
      {open && <div className="px-5 pb-5 pt-1 border-t border-gray-100">{children}</div>}
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">{label}</label>
      {children}
      {hint && <p className="text-xs text-gray-400 mt-0.5">{hint}</p>}
    </div>
  );
}

const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300";
const selectCls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-300";

function AccommodationSection({ value, onChange, cityLabel, distanceLabel }) {
  const nights = calcNights(value.check_in, value.check_out);
  const handleChange = (field, val) => {
    const updated = { ...value, [field]: val };
    updated.nights = calcNights(updated.check_in, updated.check_out);
    onChange(updated);
  };
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Hotel Name">
          <input className={inputCls} value={value.hotel_name || ""} onChange={e => handleChange("hotel_name", e.target.value)} placeholder={`Hotel in ${cityLabel}`} />
        </Field>
        <Field label="City">
          <input className={inputCls} value={value.city || ""} onChange={e => handleChange("city", e.target.value)} placeholder={cityLabel} />
        </Field>
        <Field label="Check-in">
          <input type="date" className={inputCls} value={value.check_in || ""} onChange={e => handleChange("check_in", e.target.value)} />
        </Field>
        <Field label="Check-out">
          <input type="date" className={inputCls} value={value.check_out || ""} onChange={e => handleChange("check_out", e.target.value)} />
        </Field>
      </div>
      {nights > 0 && (
        <p className="text-sm text-amber-700 font-semibold">
          {nights} night{nights !== 1 ? "s" : ""} in {cityLabel}
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Field label="Star Rating">
          <select className={selectCls} value={value.star_rating || ""} onChange={e => handleChange("star_rating", e.target.value)}>
            <option value="">— Select —</option>
            {["3★", "4★", "5★", "5★ Deluxe"].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Room Type">
          <select className={selectCls} value={value.room_type || ""} onChange={e => handleChange("room_type", e.target.value)}>
            <option value="">— Select —</option>
            {["Standard", "Superior", "Deluxe", "Suite", "Executive"].map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Meal Plan">
          <select className={selectCls} value={value.meal_plan || ""} onChange={e => handleChange("meal_plan", e.target.value)}>
            <option value="">— Select —</option>
            {["Room Only", "Bed & Breakfast", "Half Board", "Full Board", "All Inclusive"].map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </Field>
      </div>
      <div className="flex items-center gap-4 flex-wrap">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!value.breakfast_included} onChange={e => handleChange("breakfast_included", e.target.checked)} className="w-4 h-4 accent-amber-600" />
          <span className="text-sm font-medium text-gray-700">Breakfast Included</span>
        </label>
        <Field label={distanceLabel}>
          <input className={inputCls} value={value.distance_from_haram || ""} onChange={e => handleChange("distance_from_haram", e.target.value)} placeholder="e.g. 200m" />
        </Field>
      </div>
      <div className="mt-2 pt-3 border-t border-gray-100 space-y-3">
        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Internal — Not Shown to Customers</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Supplier">
            <input className={inputCls} value={value.supplier || ""} onChange={e => handleChange("supplier", e.target.value)} />
          </Field>
          <Field label="Supplier Reference">
            <input className={inputCls} value={value.supplier_reference || ""} onChange={e => handleChange("supplier_reference", e.target.value)} />
          </Field>
          <Field label="Supplier Cost / Person (€)">
            <input type="number" step="0.01" className={inputCls} value={value.supplier_cost_per_person || ""} onChange={e => handleChange("supplier_cost_per_person", e.target.value)} />
          </Field>
        </div>
        <Field label="Notes">
          <textarea rows={2} className={inputCls} value={value.notes || ""} onChange={e => handleChange("notes", e.target.value)} />
        </Field>
      </div>
    </div>
  );
}

export default function UmrahPackageDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const isNew = id === "new";

  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [pkg, setPkg] = useState(null);

  // Section open/closed state
  const [sections, setSections] = useState({
    basic: true, dates: true, makkah: true, madinah: true, flights: false,
    visa: false, transport: false, services: false, pricing: true,
    inclusions: false, terms: false, itinerary: false, internal: false,
  });
  const toggleSection = key => setSections(s => ({ ...s, [key]: !s[key] }));

  const showMsg = (text, type = "success") => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 4000);
  };

  const load = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/umrah-packages/${id}`, { headers: getAuthHeaders() });
      const d = r.data;
      setPkg(d);
      // Merge with defaults for any missing fields
      setForm({
        ...INITIAL_FORM,
        ...d,
        makkah: { ...EMPTY_ACCOMMODATION, ...(d.makkah || {}) },
        madinah: { ...EMPTY_ACCOMMODATION, ...(d.madinah || {}) },
        flight: { ...EMPTY_FLIGHT, ...(d.flight || {}) },
        occupancy_single: { ...EMPTY_OCC, ...(d.occupancy_single || {}) },
        occupancy_double: { ...EMPTY_OCC, ...(d.occupancy_double || {}) },
        occupancy_triple: { ...EMPTY_OCC, ...(d.occupancy_triple || {}) },
        occupancy_quad: { ...EMPTY_OCC, ...(d.occupancy_quad || {}) },
        transport_components: d.transport_components || [],
        other_services: d.other_services || [],
        inclusions: d.inclusions || [],
        exclusions: d.exclusions || [],
      });
    } catch { showMsg("Failed to load package.", "error"); }
    finally { setLoading(false); }
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  // Convert empty-string numeric fields to null before sending to the API
  // (Pydantic Optional[float] rejects "" but accepts null/None)
  const preparePayload = (f) => {
    const numOrNull = (v) => {
      if (v === "" || v === null || v === undefined) return null;
      const n = parseFloat(v);
      return isNaN(n) ? null : n;
    };
    const cleanAcc = (a) => a ? {
      ...a,
      supplier_cost_per_person: numOrNull(a.supplier_cost_per_person),
    } : a;
    const cleanOcc = (o) => o ? {
      ...o,
      selling_price: numOrNull(o.selling_price),
      supplier_cost: numOrNull(o.supplier_cost),
    } : o;
    return {
      ...f,
      visa_supplier_cost_per_person: numOrNull(f.visa_supplier_cost_per_person),
      makkah: cleanAcc(f.makkah),
      madinah: cleanAcc(f.madinah),
      flight: f.flight ? { ...f.flight, supplier_cost_per_person: numOrNull(f.flight.supplier_cost_per_person) } : f.flight,
      transport_components: (f.transport_components || []).map(t => ({
        ...t,
        capacity: numOrNull(t.capacity),
        supplier_cost_per_person: numOrNull(t.supplier_cost_per_person),
        selling_allocation_per_person: numOrNull(t.selling_allocation_per_person),
      })),
      other_services: (f.other_services || []).map(s => ({
        ...s,
        supplier_cost_per_person: numOrNull(s.supplier_cost_per_person),
        selling_allocation_per_person: numOrNull(s.selling_allocation_per_person),
      })),
      occupancy_single: cleanOcc(f.occupancy_single),
      occupancy_double: cleanOcc(f.occupancy_double),
      occupancy_triple: cleanOcc(f.occupancy_triple),
      occupancy_quad: cleanOcc(f.occupancy_quad),
    };
  };

  const handleSave = async () => {
    if (!form.package_name.trim()) { showMsg("Package name is required.", "error"); return; }
    setSaving(true);
    const payload = preparePayload(form);
    try {
      if (isNew) {
        const r = await axios.post(`${API}/umrah-packages`, payload, { headers: getAuthHeaders() });
        showMsg("Package created!");
        navigate(`/umrah-packages/${r.data.id}`, { replace: true });
      } else {
        const r = await axios.patch(`${API}/umrah-packages/${id}`, payload, { headers: getAuthHeaders() });
        setPkg(r.data);
        showMsg("Package saved.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      showMsg(typeof detail === "string" ? detail : "Failed to save package.", "error");
    }
    finally { setSaving(false); }
  };

  const handleDuplicate = async () => {
    if (!pkg) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/umrah-packages/${id}/duplicate`, {}, { headers: getAuthHeaders() });
      showMsg("Package duplicated — opening copy.");
      navigate(`/umrah-packages/${r.data.id}`);
    } catch { showMsg("Failed to duplicate.", "error"); }
    finally { setSaving(false); }
  };

  const handleArchive = async () => {
    if (!pkg || !window.confirm(`Archive "${pkg.package_name}"?`)) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/umrah-packages/${id}/status`, { status: "archived" }, { headers: getAuthHeaders() });
      showMsg("Package archived.");
      await load();
    } catch { showMsg("Failed to archive.", "error"); }
    finally { setSaving(false); }
  };

  const handleStatus = async (status) => {
    if (!pkg) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/umrah-packages/${id}/status`, { status }, { headers: getAuthHeaders() });
      showMsg(`Status set to ${status}.`);
      await load();
    } catch { showMsg("Failed to update status.", "error"); }
    finally { setSaving(false); }
  };

  // Derived totals for pricing summary
  const makkahNights = calcNights(form.makkah?.check_in, form.makkah?.check_out);
  const madinahNights = calcNights(form.madinah?.check_in, form.madinah?.check_out);
  const totalNights = makkahNights + madinahNights;

  const calcTotalSupplierCost = () => {
    let total = 0;
    const acc = (v) => { const n = parseFloat(v); if (!isNaN(n)) total += n; };
    if (form.visa_included) acc(form.visa_supplier_cost_per_person);
    if (form.flights_included) acc(form.flight?.supplier_cost_per_person);
    acc(form.makkah?.supplier_cost_per_person);
    acc(form.madinah?.supplier_cost_per_person);
    form.transport_components.forEach(t => acc(t.supplier_cost_per_person));
    form.other_services.forEach(s => acc(s.supplier_cost_per_person));
    return total;
  };

  const totalSupplier = calcTotalSupplierCost();

  // Section-9 live calculation preview
  const [previewOcc, setPreviewOcc]         = useState("double");
  const [previewTravellers, setPreviewTravellers] = useState(2);

  // Inclusions helpers
  const addItem = (field) => set(field, [...(form[field] || []), ""]);
  const updateItem = (field, idx, val) => set(field, form[field].map((x, i) => i === idx ? val : x));
  const removeItem = (field, idx) => set(field, form[field].filter((_, i) => i !== idx));

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 max-w-4xl mx-auto space-y-5 pb-16">

        {/* Header */}
        <div className="flex items-center gap-3 flex-wrap justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/umrah-packages")} className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
              <ArrowLeft size={18} />
            </button>
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
              <Moon className="w-5 h-5 text-amber-700" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                {isNew ? "New Umrah Package" : (form.package_name || "Umrah Package")}
              </h1>
              {!isNew && pkg && (
                <p className="text-xs font-mono text-amber-700 font-semibold">{pkg.package_reference}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {!isNew && pkg && pkg.status !== "archived" && (
              <>
                <button
                  onClick={handleDuplicate}
                  disabled={saving}
                  className="flex items-center gap-2 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg px-3 py-2 text-sm font-semibold disabled:opacity-50"
                >
                  <Copy size={14} /> Duplicate
                </button>
                <button
                  onClick={handleArchive}
                  disabled={saving}
                  className="flex items-center gap-2 border border-red-200 hover:bg-red-50 text-red-600 rounded-lg px-3 py-2 text-sm font-semibold disabled:opacity-50"
                >
                  <Archive size={14} /> Archive
                </button>
              </>
            )}
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50"
            >
              <Save size={14} /> {saving ? "Saving…" : "Save Package"}
            </button>
          </div>
        </div>

        {msg && (
          <div className={`rounded-lg px-4 py-3 text-sm font-semibold ${msg.type === "error" ? "bg-red-50 text-red-700 border border-red-200" : "bg-green-50 text-green-700 border border-green-200"}`}>
            {msg.text}
          </div>
        )}

        {/* ── SECTION 1: Basic Information ───────────────────────────── */}
        <Section title="1. Basic Information" icon={Package} open={sections.basic} onToggle={() => toggleSection("basic")}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
            <Field label="Package Name" hint="e.g. 14 Nights Premium Umrah — November 2026">
              <input className={inputCls} value={form.package_name} onChange={e => set("package_name", e.target.value)} placeholder="Package name" />
            </Field>
            <Field label="Category">
              <select className={selectCls} value={form.category} onChange={e => set("category", e.target.value)}>
                <option value="comprehensive">Comprehensive</option>
                <option value="without_visa">Without Visa</option>
                <option value="custom">Custom</option>
                <option value="land_only">Land Only</option>
                <option value="other">Other</option>
              </select>
            </Field>
            <Field label="Status">
              <select className={selectCls} value={form.status} onChange={e => set("status", e.target.value)}>
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="archived">Archived</option>
              </select>
            </Field>
            <Field label="Public Visible">
              <div className="flex items-center gap-3 pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!form.public_visible} onChange={e => set("public_visible", e.target.checked)} className="w-4 h-4 accent-amber-600" />
                  <span className="text-sm text-gray-700">Show on public website (Phase 2)</span>
                </label>
              </div>
            </Field>
          </div>
        </Section>

        {/* ── SECTION 2: Travel Dates ──────────────────────────────── */}
        <Section title="2. Travel Dates" icon={Tag} open={sections.dates} onToggle={() => toggleSection("dates")}>
          <div className="mt-3 space-y-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!form.flexible_dates} onChange={e => set("flexible_dates", e.target.checked)} className="w-4 h-4 accent-amber-600" />
              <span className="text-sm font-medium text-gray-700">Flexible dates (not tied to specific period)</span>
            </label>
            {!form.flexible_dates && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Travel Start Date">
                  <input type="date" className={inputCls} value={form.travel_start_date || ""} onChange={e => set("travel_start_date", e.target.value)} />
                </Field>
                <Field label="Travel End Date">
                  <input type="date" className={inputCls} value={form.travel_end_date || ""} onChange={e => set("travel_end_date", e.target.value)} />
                </Field>
              </div>
            )}
            {totalNights > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 font-semibold">
                Total nights: {totalNights}
                {makkahNights > 0 && ` (${makkahNights} Makkah`}
                {madinahNights > 0 && ` + ${madinahNights} Madinah`}
                {(makkahNights > 0 || madinahNights > 0) && ")"}
              </div>
            )}
          </div>
        </Section>

        {/* ── SECTION 3: Makkah ────────────────────────────────────── */}
        <Section title="3. Makkah Accommodation" icon={BedDouble} open={sections.makkah} onToggle={() => toggleSection("makkah")}>
          <div className="mt-3">
            <AccommodationSection
              value={form.makkah}
              onChange={v => set("makkah", v)}
              cityLabel="Makkah"
              distanceLabel="Distance from Haram"
            />
          </div>
        </Section>

        {/* ── SECTION 4: Madinah ───────────────────────────────────── */}
        <Section title="4. Madinah Accommodation (Optional)" icon={BedDouble} open={sections.madinah} onToggle={() => toggleSection("madinah")}>
          <div className="mt-3">
            <AccommodationSection
              value={form.madinah}
              onChange={v => set("madinah", v)}
              cityLabel="Madinah"
              distanceLabel="Distance from Masjid an-Nabawi"
            />
          </div>
        </Section>

        {/* ── SECTION 5: Flights ───────────────────────────────────── */}
        <Section title="5. Flights" icon={Plane} open={sections.flights} onToggle={() => toggleSection("flights")}>
          <div className="mt-3 space-y-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!form.flights_included} onChange={e => set("flights_included", e.target.checked)} className="w-4 h-4 accent-amber-600" />
              <span className="text-sm font-medium text-gray-700">Flights Included in Package</span>
            </label>
            {form.flights_included && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Outbound Route">
                    <input className={inputCls} value={form.flight?.outbound_route || ""} onChange={e => set("flight", { ...form.flight, outbound_route: e.target.value })} placeholder="e.g. Amsterdam → Jeddah" />
                  </Field>
                  <Field label="Return Route">
                    <input className={inputCls} value={form.flight?.return_route || ""} onChange={e => set("flight", { ...form.flight, return_route: e.target.value })} placeholder="e.g. Madinah → Amsterdam" />
                  </Field>
                  <Field label="Airline">
                    <input className={inputCls} value={form.flight?.airline || ""} onChange={e => set("flight", { ...form.flight, airline: e.target.value })} />
                  </Field>
                  <Field label="Flight Numbers">
                    <input className={inputCls} value={form.flight?.flight_numbers || ""} onChange={e => set("flight", { ...form.flight, flight_numbers: e.target.value })} placeholder="e.g. KL 0551 / KL 0552" />
                  </Field>
                  <Field label="Outbound Date">
                    <input type="date" className={inputCls} value={form.flight?.outbound_date || ""} onChange={e => set("flight", { ...form.flight, outbound_date: e.target.value })} />
                  </Field>
                  <Field label="Return Date">
                    <input type="date" className={inputCls} value={form.flight?.return_date || ""} onChange={e => set("flight", { ...form.flight, return_date: e.target.value })} />
                  </Field>
                </div>
                <div className="pt-3 border-t border-gray-100 space-y-3">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Internal — Not Shown to Customers</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Supplier">
                      <input className={inputCls} value={form.flight?.supplier || ""} onChange={e => set("flight", { ...form.flight, supplier: e.target.value })} />
                    </Field>
                    <Field label="Supplier Cost / Person (€)">
                      <input type="number" step="0.01" className={inputCls} value={form.flight?.supplier_cost_per_person || ""} onChange={e => set("flight", { ...form.flight, supplier_cost_per_person: e.target.value })} />
                    </Field>
                  </div>
                  <Field label="Notes">
                    <textarea rows={2} className={inputCls} value={form.flight?.notes || ""} onChange={e => set("flight", { ...form.flight, notes: e.target.value })} />
                  </Field>
                </div>
              </>
            )}
          </div>
        </Section>

        {/* ── SECTION 6: Visa ──────────────────────────────────────── */}
        <Section title="6. Visa" icon={CheckCircle} open={sections.visa} onToggle={() => toggleSection("visa")}>
          <div className="mt-3 space-y-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!form.visa_included} onChange={e => set("visa_included", e.target.checked)} className="w-4 h-4 accent-amber-600" />
              <span className="text-sm font-medium text-gray-700">Umrah Visa Included</span>
            </label>
            {form.visa_included && (
              <div className="pt-3 border-t border-gray-100">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Internal — Not Shown to Customers</p>
                <Field label="Visa Supplier Cost / Person (€)">
                  <input type="number" step="0.01" className={`${inputCls} max-w-xs`} value={form.visa_supplier_cost_per_person || ""} onChange={e => set("visa_supplier_cost_per_person", e.target.value)} />
                </Field>
              </div>
            )}
          </div>
        </Section>

        {/* ── SECTION 7: Transport ─────────────────────────────────── */}
        <Section title="7. Transport" icon={Car} open={sections.transport} onToggle={() => toggleSection("transport")}>
          <div className="mt-3 space-y-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!form.transport_included} onChange={e => set("transport_included", e.target.checked)} className="w-4 h-4 accent-amber-600" />
              <span className="text-sm font-medium text-gray-700">Transport Included in Package</span>
            </label>
            {form.transport_components.map((tc, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-700">Transport {idx + 1}</span>
                  <button type="button" onClick={() => set("transport_components", form.transport_components.filter((_, i) => i !== idx))} className="text-red-400 hover:text-red-600">
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Type">
                    <select className={selectCls} value={tc.transport_type || ""} onChange={e => set("transport_components", form.transport_components.map((t, i) => i === idx ? { ...t, transport_type: e.target.value } : t))}>
                      <option value="">— Select —</option>
                      {["Airport Transfer", "Intercity Transfer", "Local Transfer", "Other"].map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </Field>
                  <Field label="Route">
                    <input className={inputCls} value={tc.route || ""} onChange={e => set("transport_components", form.transport_components.map((t, i) => i === idx ? { ...t, route: e.target.value } : t))} placeholder="e.g. Jeddah Airport → Makkah" />
                  </Field>
                  <Field label="Supplier">
                    <input className={inputCls} value={tc.supplier || ""} onChange={e => set("transport_components", form.transport_components.map((t, i) => i === idx ? { ...t, supplier: e.target.value } : t))} />
                  </Field>
                  <Field label="Supplier Cost / Person (€)">
                    <input type="number" step="0.01" className={inputCls} value={tc.supplier_cost_per_person || ""} onChange={e => set("transport_components", form.transport_components.map((t, i) => i === idx ? { ...t, supplier_cost_per_person: e.target.value } : t))} />
                  </Field>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => set("transport_components", [...form.transport_components, { ...EMPTY_TRANSPORT }])}
              className="flex items-center gap-2 text-sm text-amber-700 hover:text-amber-900 font-semibold"
            >
              <Plus size={14} /> Add Transport Component
            </button>
          </div>
        </Section>

        {/* ── SECTION 8: Other Services ────────────────────────────── */}
        <Section title="8. Other Services" icon={AlignLeft} open={sections.services} onToggle={() => toggleSection("services")}>
          <div className="mt-3 space-y-4">
            <p className="text-xs text-gray-500">Ziyarah, Catering, Guide, Meet &amp; Assist, SIM, Wheelchair, etc.</p>
            {form.other_services.map((svc, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-700">Service {idx + 1}</span>
                  <button type="button" onClick={() => set("other_services", form.other_services.filter((_, i) => i !== idx))} className="text-red-400 hover:text-red-600">
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Service Name">
                    <input className={inputCls} value={svc.service_name || ""} onChange={e => set("other_services", form.other_services.map((s, i) => i === idx ? { ...s, service_name: e.target.value } : s))} placeholder="e.g. Ziyarah" />
                  </Field>
                  <div className="flex items-center gap-3 pt-5">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={!!svc.included} onChange={e => set("other_services", form.other_services.map((s, i) => i === idx ? { ...s, included: e.target.checked } : s))} className="w-4 h-4 accent-amber-600" />
                      <span className="text-sm text-gray-700">Included</span>
                    </label>
                  </div>
                  <Field label="Description">
                    <input className={inputCls} value={svc.description || ""} onChange={e => set("other_services", form.other_services.map((s, i) => i === idx ? { ...s, description: e.target.value } : s))} />
                  </Field>
                  <Field label="Supplier Cost / Person (€)">
                    <input type="number" step="0.01" className={inputCls} value={svc.supplier_cost_per_person || ""} onChange={e => set("other_services", form.other_services.map((s, i) => i === idx ? { ...s, supplier_cost_per_person: e.target.value } : s))} />
                  </Field>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => set("other_services", [...form.other_services, { ...EMPTY_SERVICE }])}
              className="flex items-center gap-2 text-sm text-amber-700 hover:text-amber-900 font-semibold"
            >
              <Plus size={14} /> Add Service
            </button>
          </div>
        </Section>

        {/* ── SECTION 9: Customer Pricing by Room Occupancy ────────── */}
        <Section title="9. Customer Pricing by Room Occupancy" icon={Tag} open={sections.pricing} onToggle={() => toggleSection("pricing")}>
          <div className="mt-3 space-y-5">
            <p className="text-xs text-gray-500">
              Set the final all-inclusive Umrah package price per person for each room-sharing option.
              Supplier costs are tracked separately for internal profit analysis.
            </p>

            {/* Occupancy cards */}
            {[
              { key: "occupancy_single", oKey: "single" },
              { key: "occupancy_double", oKey: "double" },
              { key: "occupancy_triple", oKey: "triple" },
              { key: "occupancy_quad",   oKey: "quad"   },
            ].map(({ key, oKey }) => {
              const capacity = OCC_CAPACITY[oKey];
              const label    = OCC_LABEL[oKey];
              const occ = form[key] || EMPTY_OCC;
              const profit = calcOccupancyProfit(occ);
              const sp = parseFloat(occ.selling_price);
              const belowCost = !isNaN(sp) && totalSupplier > 0 && sp < totalSupplier;
              return (
                <div key={key} className={`border rounded-lg p-4 ${occ.enabled ? "border-amber-200 bg-amber-50/30" : "border-gray-200"}`}>
                  <label className="flex items-center gap-2 cursor-pointer mb-1">
                    <input
                      type="checkbox"
                      checked={!!occ.enabled}
                      onChange={e => set(key, { ...occ, enabled: e.target.checked })}
                      className="w-4 h-4 accent-amber-600"
                    />
                    <span className="text-sm font-bold text-gray-900">{label}</span>
                    <span className="text-xs text-gray-500">{capacity} person{capacity > 1 ? "s" : ""} / room</span>
                    {occ.enabled && occ.selling_price && (
                      <span className="ml-auto text-sm text-amber-700 font-bold">€{parseFloat(occ.selling_price).toLocaleString("en-GB", { minimumFractionDigits: 0 })}/pp</span>
                    )}
                  </label>
                  {occ.enabled && (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-3">
                        <Field label="Selling Price / Person (€)" hint="All-inclusive, customer-facing">
                          <input
                            type="number" step="0.01" min="0"
                            className={inputCls}
                            value={occ.selling_price || ""}
                            onChange={e => set(key, { ...occ, selling_price: e.target.value })}
                            placeholder="e.g. 1500"
                          />
                        </Field>
                        <Field label="Supplier Cost / Person (€)" hint="Internal only">
                          <input
                            type="number" step="0.01" min="0"
                            className={inputCls}
                            value={occ.supplier_cost || ""}
                            onChange={e => set(key, { ...occ, supplier_cost: e.target.value })}
                            placeholder="e.g. 1200"
                          />
                        </Field>
                        <Field label="Profit / Person">
                          <div className={`mt-1.5 text-sm font-bold ${profit !== null ? (profit >= 0 ? "text-green-700" : "text-red-600") : "text-gray-400"}`}>
                            {profit !== null
                              ? `€${profit.toFixed(2)}${occ.selling_price ? ` (${((profit / parseFloat(occ.selling_price)) * 100).toFixed(1)}%)` : ""}`
                              : "—"}
                          </div>
                        </Field>
                      </div>
                      {belowCost && (
                        <div className="mt-3 flex items-start gap-2 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
                          <span className="font-bold shrink-0">⚠ Below cost:</span>
                          <span>
                            Selling price €{sp.toFixed(2)}/pp is below estimated supplier cost €{totalSupplier.toFixed(2)}/pp
                            (shortfall: €{(totalSupplier - sp).toFixed(2)}/pp). You can still save — confirm this is intentional.
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}

            {/* Live calculation preview */}
            {(() => {
              const enabledOccs = ["single", "double", "triple", "quad"].filter(k => {
                const o = form[`occupancy_${k}`];
                return o?.enabled && o?.selling_price;
              });
              if (enabledOccs.length === 0) return null;
              const safeOcc = enabledOccs.includes(previewOcc) ? previewOcc : enabledOccs[0];
              const occ = form[`occupancy_${safeOcc}`];
              const sp = parseFloat(occ?.selling_price || 0);
              const cap = OCC_CAPACITY[safeOcc];
              const travs = Math.max(1, parseInt(previewTravellers) || 1);
              const rooms = Math.ceil(travs / cap);
              const total = sp * travs;
              return (
                <div className="bg-white border border-amber-300 rounded-lg p-4">
                  <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-3">Live Calculation Preview</p>
                  <div className="flex flex-wrap gap-4 mb-4">
                    <div>
                      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Occupancy</label>
                      <select
                        className={`mt-1 ${selectCls} w-auto`}
                        value={safeOcc}
                        onChange={e => setPreviewOcc(e.target.value)}
                      >
                        {enabledOccs.map(k => (
                          <option key={k} value={k}>{OCC_LABEL[k]} ({OCC_CAPACITY[k]}pp/room)</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Number of Travellers</label>
                      <input
                        type="number" min="1" max="100"
                        className={`mt-1 ${inputCls} w-32`}
                        value={previewTravellers}
                        onChange={e => setPreviewTravellers(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="bg-amber-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-gray-500 mb-0.5">Occupancy</p>
                      <p className="text-sm font-bold text-amber-800">{OCC_LABEL[safeOcc]}</p>
                      <p className="text-xs text-gray-500">{cap} person{cap > 1 ? "s" : ""}/room</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-gray-500 mb-0.5">Rooms Required</p>
                      <p className="text-sm font-bold text-amber-800">{rooms}</p>
                      <p className="text-xs text-gray-500">⌈{travs} ÷ {cap}⌉</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-gray-500 mb-0.5">Price / Person</p>
                      <p className="text-sm font-bold text-amber-800">€{sp.toLocaleString("en-GB", { minimumFractionDigits: 0 })}</p>
                    </div>
                    <div className="bg-amber-600 rounded-lg p-3 text-center">
                      <p className="text-xs text-amber-100 mb-0.5">Package Total</p>
                      <p className="text-base font-bold text-white">€{total.toLocaleString("en-GB", { minimumFractionDigits: 0 })}</p>
                      <p className="text-xs text-amber-200">{travs} × €{sp.toLocaleString("en-GB", { minimumFractionDigits: 0 })}</p>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Internal cost breakdown */}
            {totalSupplier > 0 && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm space-y-1">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Internal Cost Breakdown (per person)</p>
                {form.makkah?.supplier_cost_per_person && <p className="flex justify-between"><span className="text-gray-600">Makkah Hotel</span><span className="font-mono">{fmtMoney(form.makkah.supplier_cost_per_person)}</span></p>}
                {form.madinah?.supplier_cost_per_person && <p className="flex justify-between"><span className="text-gray-600">Madinah Hotel</span><span className="font-mono">{fmtMoney(form.madinah.supplier_cost_per_person)}</span></p>}
                {form.flights_included && form.flight?.supplier_cost_per_person && <p className="flex justify-between"><span className="text-gray-600">Flights</span><span className="font-mono">{fmtMoney(form.flight.supplier_cost_per_person)}</span></p>}
                {form.visa_included && form.visa_supplier_cost_per_person && <p className="flex justify-between"><span className="text-gray-600">Visa</span><span className="font-mono">{fmtMoney(form.visa_supplier_cost_per_person)}</span></p>}
                {form.transport_components.filter(t => t.supplier_cost_per_person).map((t, i) => (
                  <p key={i} className="flex justify-between"><span className="text-gray-600">{t.route || `Transport ${i + 1}`}</span><span className="font-mono">{fmtMoney(t.supplier_cost_per_person)}</span></p>
                ))}
                {form.other_services.filter(s => s.supplier_cost_per_person).map((s, i) => (
                  <p key={i} className="flex justify-between"><span className="text-gray-600">{s.service_name || `Service ${i + 1}`}</span><span className="font-mono">{fmtMoney(s.supplier_cost_per_person)}</span></p>
                ))}
                <p className="flex justify-between font-bold text-gray-900 border-t border-gray-200 pt-1 mt-1">
                  <span>Total Supplier Cost</span>
                  <span className="font-mono">{fmtMoney(totalSupplier)}</span>
                </p>
              </div>
            )}

            {/* Per-occupancy pricing summary table — internal agents only */}
            {(() => {
              const rows = ["single", "double", "triple", "quad"]
                .filter(k => form[`occupancy_${k}`]?.enabled && form[`occupancy_${k}`]?.selling_price)
                .map(k => ({ k, label: OCC_LABEL[k], cap: OCC_CAPACITY[k], sp: parseFloat(form[`occupancy_${k}`].selling_price) }));
              if (rows.length === 0) return null;
              return (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm">
                  <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-3">Pricing Summary (Internal)</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-gray-500 border-b border-amber-200">
                          <th className="text-left pb-1 pr-3">Occupancy</th>
                          <th className="text-center pb-1 pr-3">Persons/Room</th>
                          <th className="text-right pb-1 pr-3">Selling Price/pp</th>
                          {totalSupplier > 0 && <th className="text-right pb-1 pr-3">Est. Cost/pp</th>}
                          {totalSupplier > 0 && <th className="text-right pb-1 pr-3">Est. Profit/pp</th>}
                          {totalSupplier > 0 && <th className="text-right pb-1">Margin %</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map(({ k, label, cap, sp }) => {
                          const profit = totalSupplier > 0 ? sp - totalSupplier : null;
                          const margin = (profit !== null && sp > 0) ? ((profit / sp) * 100) : null;
                          const belowCost = profit !== null && profit < 0;
                          return (
                            <tr key={k} className={`border-b border-amber-100 last:border-0 ${belowCost ? "text-red-700" : "text-gray-800"}`}>
                              <td className="py-1 pr-3 font-semibold">{label}</td>
                              <td className="py-1 pr-3 text-center text-gray-500">{cap}</td>
                              <td className="py-1 pr-3 text-right font-mono">€{sp.toFixed(2)}</td>
                              {totalSupplier > 0 && <td className="py-1 pr-3 text-right font-mono text-gray-500">€{totalSupplier.toFixed(2)}</td>}
                              {totalSupplier > 0 && (
                                <td className={`py-1 pr-3 text-right font-mono font-bold ${belowCost ? "text-red-700" : "text-green-700"}`}>
                                  {belowCost ? "–" : "+"}€{Math.abs(profit).toFixed(2)}
                                </td>
                              )}
                              {totalSupplier > 0 && (
                                <td className={`py-1 text-right font-mono font-bold ${belowCost ? "text-red-700" : "text-green-700"}`}>
                                  {margin !== null ? `${margin.toFixed(1)}%` : "—"}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}
          </div>
        </Section>

        {/* ── SECTION 10: Inclusions & Exclusions ──────────────────── */}
        <Section title="10. Inclusions & Exclusions" icon={CheckCircle} open={sections.inclusions} onToggle={() => toggleSection("inclusions")}>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <p className="text-xs font-bold text-green-700 uppercase tracking-wider flex items-center gap-1"><CheckCircle size={12} /> Included</p>
              {(form.inclusions || []).map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input className={`${inputCls} flex-1`} value={item} onChange={e => updateItem("inclusions", idx, e.target.value)} placeholder="e.g. Umrah visa" />
                  <button type="button" onClick={() => removeItem("inclusions", idx)} className="text-red-400 hover:text-red-600"><Trash2 size={13} /></button>
                </div>
              ))}
              <button type="button" onClick={() => addItem("inclusions")} className="text-xs text-green-700 hover:text-green-900 font-semibold flex items-center gap-1">
                <Plus size={12} /> Add item
              </button>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-bold text-red-600 uppercase tracking-wider flex items-center gap-1"><XCircle size={12} /> Excluded</p>
              {(form.exclusions || []).map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input className={`${inputCls} flex-1`} value={item} onChange={e => updateItem("exclusions", idx, e.target.value)} placeholder="e.g. Personal expenses" />
                  <button type="button" onClick={() => removeItem("exclusions", idx)} className="text-red-400 hover:text-red-600"><Trash2 size={13} /></button>
                </div>
              ))}
              <button type="button" onClick={() => addItem("exclusions")} className="text-xs text-red-600 hover:text-red-900 font-semibold flex items-center gap-1">
                <Plus size={12} /> Add item
              </button>
            </div>
          </div>
        </Section>

        {/* ── SECTION 11: Terms & Conditions ───────────────────────── */}
        <Section title="11. Terms & Conditions" icon={AlignLeft} open={sections.terms} onToggle={() => toggleSection("terms")}>
          <div className="mt-3 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Validity Date">
                <input type="date" className={inputCls} value={form.validity_date || ""} onChange={e => set("validity_date", e.target.value)} />
              </Field>
              <Field label="Payment Terms">
                <input className={inputCls} value={form.payment_terms || ""} onChange={e => set("payment_terms", e.target.value)} placeholder="e.g. 50% deposit, balance 30 days before" />
              </Field>
            </div>
            <Field label="Cancellation Policy">
              <textarea rows={3} className={inputCls} value={form.cancellation_policy || ""} onChange={e => set("cancellation_policy", e.target.value)} />
            </Field>
            <Field label="Terms & Conditions">
              <textarea rows={4} className={inputCls} value={form.terms_and_conditions || ""} onChange={e => set("terms_and_conditions", e.target.value)} />
            </Field>
          </div>
        </Section>

        {/* ── SECTION 12: Itinerary ────────────────────────────────── */}
        <Section title="12. General Itinerary" icon={AlignLeft} open={sections.itinerary} onToggle={() => toggleSection("itinerary")}>
          <div className="mt-3 space-y-3">
            <p className="text-xs text-gray-500">
              Generate a day-by-day itinerary from the package data, then edit freely.
              The generated itinerary is customer-facing — do not include supplier names, costs, or internal notes.
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              {!form.itinerary_notes ? (
                <button
                  type="button"
                  onClick={() => {
                    const gen = generateItinerary(form);
                    if (gen.trim()) set("itinerary_notes", gen);
                    else showMsg("Not enough package data to generate an itinerary. Fill in hotel dates and flight details first.", "error");
                  }}
                  className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg px-4 py-2 text-sm font-semibold"
                >
                  ✨ Generate Itinerary
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    if (!window.confirm("Regenerating will replace the current itinerary. Any manual edits will be lost. Proceed?")) return;
                    const gen = generateItinerary(form);
                    if (gen.trim()) set("itinerary_notes", gen);
                    else showMsg("Not enough package data to regenerate. Fill in hotel dates and flight details first.", "error");
                  }}
                  className="flex items-center gap-2 border border-amber-300 hover:bg-amber-50 text-amber-700 rounded-lg px-4 py-2 text-sm font-semibold"
                >
                  🔄 Regenerate Itinerary
                </button>
              )}
              {form.itinerary_notes && (
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm("Clear the itinerary?")) set("itinerary_notes", "");
                  }}
                  className="text-xs text-gray-400 hover:text-red-500 px-2 py-1"
                >
                  Clear
                </button>
              )}
            </div>
            <textarea
              rows={16}
              className={`${inputCls} font-mono text-xs leading-relaxed`}
              value={form.itinerary_notes || ""}
              onChange={e => set("itinerary_notes", e.target.value)}
              placeholder={"Click 'Generate Itinerary' to auto-generate from package data, or type manually.\n\nDAY 1 — ARRIVAL / MAKKAH\n28 November\n\nArrival in Jeddah.\nTransfer to Makkah.\nCheck-in at hotel."}
            />
          </div>
        </Section>

        {/* ── SECTION 13: Internal Notes ───────────────────────────── */}
        <Section title="13. Internal Notes" icon={AlignLeft} open={sections.internal} onToggle={() => toggleSection("internal")} accent="gray">
          <div className="mt-3">
            <p className="text-xs text-gray-400 mb-2">Internal notes — never shared with customers.</p>
            <textarea rows={4} className={inputCls} value={form.internal_notes || ""} onChange={e => set("internal_notes", e.target.value)} />
          </div>
        </Section>

        {/* Status quick-change (existing packages only) */}
        {!isNew && pkg && pkg.status !== "archived" && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Change Status</p>
            <div className="flex gap-2 flex-wrap">
              {["draft", "active", "inactive"].filter(s => s !== pkg.status).map(s => (
                <button
                  key={s}
                  onClick={() => handleStatus(s)}
                  disabled={saving}
                  className="px-4 py-2 rounded-lg text-sm font-semibold border border-gray-200 hover:bg-gray-50 capitalize disabled:opacity-50"
                >
                  Set as {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Save button (bottom) */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg px-6 py-2.5 text-sm font-semibold disabled:opacity-50"
          >
            <Save size={14} /> {saving ? "Saving…" : "Save Package"}
          </button>
        </div>

        {/* ── QUOTATIONS (existing packages only) ─────────────────── */}
        {!isNew && pkg && (
          <div className="bg-white rounded-xl border border-amber-100 shadow-sm p-5">
            <UmrahQuotationPanel pkg={pkg} />
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
